'use client';

import React, { type Dispatch, type SetStateAction } from 'react';

import { useTranslations } from 'next-intl';

import { EbayButton } from '@/components/EbayButton';
import { useEbayAuth } from '@/modules/auth/useEbayAuth';
import { fetchEbayUserCredentials } from '@/modules/login/api';
import { handleSSOError } from '@/modules/login/helpers';
import { LoginType } from '@/modules/login/types';
import { useLoginMutations } from '@/modules/login/useLoginMutations';
import { useLoginTracking } from '@/modules/login/useLoginTracking';
import { Stdv1Stat } from '@/modules/observability/constants';
import { recordMetric } from '@/modules/observability/metrics';

type Props = {
  setError: Dispatch<SetStateAction<string>>;
};

export function EbayLoginButton({ setError }: Props) {
  const t = useTranslations('login');
  const { sendLoginSubmitAction } = useLoginTracking();
  const { ebayLoginMutation } = useLoginMutations({
    loginType: LoginType.Ebay,
  });

  // TODO(pcosv): confirm error copy with Jonny / design (popup blocked, authorize failure, state mismatch, credentials failure)
  const errorMessage = t('Error.SocialProviderLogin', {
    socialProvider: 'eBay',
  });

  async function handleEbayAuthorized(code: string) {
    let ebay_user_id: string;
    let ebay_token_reference: string;

    try {
      ({
        data: { ebay_user_id, ebay_token_reference },
      } = await fetchEbayUserCredentials(code));
    } catch {
      recordMetric({
        type: 'increment',
        stat: Stdv1Stat['ebay.auth.credentials_error'],
      });
      return setError(errorMessage);
    }

    ebayLoginMutation.mutate(
      { ebay_user_id, ebay_token_reference },
      {
        onError: (error) => {
          handleSSOError({
            error,
            setError,
            onUserNotFound: () => {
              // WEB-9298 route to eBay signup
            },
            onAccountNotLinked: () => {
              // WEB-9299 set account linking cookies and show the account exists error
            },
          });
        },
      }
    );
  }

  const { startPopupFlow, isInProgress } = useEbayAuth({
    onEbayAuthorized: handleEbayAuthorized,
    onError: () => setError(errorMessage),
  });

  function handleClick() {
    sendLoginSubmitAction(LoginType.Ebay);
    startPopupFlow();
  }

  return (
    <EbayButton
      onClick={handleClick}
      disabled={isInProgress || ebayLoginMutation.isPending}
    />
  );
}
